import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DatePicker } from "@/components/DatePicker";
import { CalendarPlus, Plus, Trash, PencilSimple, ArrowRight, Info } from "@phosphor-icons/react";
import { format } from "date-fns";
import { toast } from "sonner";

const TYPE_GROUPS = [
  {
    label: "Base Pay",
    types: [
      { key: "annual_salary", label: "Annual Salary" },
      { key: "hourly_rate", label: "Hourly Rate" },
      { key: "regular_scheduled_hours", label: "Regular Scheduled Hours" },
    ],
  },
  {
    label: "Earning Rates (forward-only)",
    types: [
      { key: "overtime_rate", label: "Overtime Rate" },
      { key: "paid_duty_rate", label: "Paid Duty Rate" },
      { key: "court_rate", label: "Court Appearance Rate" },
      { key: "shift_premium", label: "Shift Premium" },
      { key: "bonus", label: "Bonus" },
    ],
  },
  { label: "Deductions", types: [{ key: "deduction", label: "Deduction (tax, CPP, EI, pension, union…)" }] },
];
const TYPE_LABEL = Object.fromEntries(TYPE_GROUPS.flatMap((g) => g.types).map((t) => [t.key, t.label]));
const FORWARD_ONLY = new Set(["overtime_rate", "paid_duty_rate", "court_rate", "shift_premium", "bonus"]);

const emptyForm = {
  change_type: "annual_salary",
  effective_date: format(new Date(), "yyyy-MM-dd"),
  end_date: "",
  new_value: "",
  previous_value: "",
  target_id: "",
  notes: "",
};

const fmtDate = (s) => (s ? format(new Date(s + "T00:00:00"), "MMM d, yyyy") : null);

export default function PayChanges() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const currency = user?.currency || "CAD";

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const { data: changes = [] } = useQuery({
    queryKey: ["pay-changes"],
    queryFn: async () => (await api.get("/pay-changes")).data,
  });
  const { data: deductions = [] } = useQuery({
    queryKey: ["deductions"],
    queryFn: async () => (await api.get("/deductions")).data,
  });

  const dedName = (id) => deductions.find((d) => d.id === id)?.name || "Deduction";

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["pay-changes"] });
    qc.invalidateQueries({ queryKey: ["projections"] });
  };

  const buildBody = (f) => ({
    change_type: f.change_type,
    effective_date: f.effective_date,
    end_date: f.end_date || null,
    new_value: parseFloat(f.new_value) || 0,
    previous_value: f.previous_value === "" ? null : parseFloat(f.previous_value),
    target_id: f.change_type === "deduction" ? f.target_id || null : null,
    notes: f.notes || "",
  });

  const saveMut = useMutation({
    mutationFn: async (f) => {
      const body = buildBody(f);
      if (editing) return (await api.put(`/pay-changes/${editing}`, body)).data;
      return (await api.post("/pay-changes", body)).data;
    },
    onSuccess: () => {
      invalidate();
      toast.success(editing ? "Pay change updated." : "Pay change scheduled.");
      setOpen(false); setEditing(null); setForm(emptyForm);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => api.delete(`/pay-changes/${id}`),
    onSuccess: () => { invalidate(); toast.success("Pay change removed."); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c.id);
    setForm({
      change_type: c.change_type,
      effective_date: c.effective_date,
      end_date: c.end_date || "",
      new_value: String(c.new_value ?? ""),
      previous_value: c.previous_value == null ? "" : String(c.previous_value),
      target_id: c.target_id || "",
      notes: c.notes || "",
    });
    setOpen(true);
  };

  const valueLabel = (c) =>
    c.change_type === "regular_scheduled_hours" ? `${c.new_value} h` : money(c.new_value, currency);
  const changeTitle = (c) =>
    c.change_type === "deduction" ? `${dedName(c.target_id)} deduction` : TYPE_LABEL[c.change_type] || c.change_type;

  const submit = (e) => {
    e.preventDefault();
    if (form.change_type === "deduction" && !form.target_id) {
      toast.error("Select which deduction this change applies to.");
      return;
    }
    saveMut.mutate(form);
  };

  return (
    <div className="animate-fade-up" data-testid="pay-changes-page">
      <div className="mb-8">
        <div className="flex items-center gap-2">
          <CalendarPlus className="h-5 w-5 text-sky-400" weight="bold" />
          <p className="overline text-sky-400">Forecast settings</p>
        </div>
        <h1 className="text-3xl font-black tracking-tight mt-1">Future Pay Changes</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Schedule raises, acting pay and deduction changes. Your Projections automatically use the settings in effect on each pay date.
        </p>
      </div>

      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-bold">Scheduled changes</h2>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(emptyForm); } }}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="add-pay-change-button" size="sm" className="rounded-md font-semibold">
              <Plus className="h-4 w-4 mr-1.5" weight="bold" /> Schedule a change
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-popover border-border max-h-[90vh] overflow-y-auto" data-testid="pay-change-dialog">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit pay change" : "Schedule a pay change"}</DialogTitle>
              <DialogDescription className="text-xs">Takes effect automatically on the effective date in your projections.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Change type</Label>
                <Select value={form.change_type} onValueChange={(v) => setForm({ ...form, change_type: v })}>
                  <SelectTrigger className="mt-1.5 bg-secondary/50" data-testid="pay-change-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPE_GROUPS.map((g) => (
                      <div key={g.label}>
                        <p className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground">{g.label}</p>
                        {g.types.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {form.change_type === "deduction" && (
                <div>
                  <Label>Which deduction</Label>
                  <Select value={form.target_id} onValueChange={(v) => setForm({ ...form, target_id: v })}>
                    <SelectTrigger className="mt-1.5 bg-secondary/50" data-testid="pay-change-deduction-select">
                      <SelectValue placeholder="Select a deduction" />
                    </SelectTrigger>
                    <SelectContent>
                      {deductions.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} ({d.kind === "percent" ? `${d.value}%` : money(d.value, currency)})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {FORWARD_ONLY.has(form.change_type) && (
                <div className="flex items-start gap-2 rounded-md bg-amber-500/10 border border-amber-500/30 p-3 text-xs text-amber-300" data-testid="pay-change-forward-note">
                  <Info className="h-4 w-4 shrink-0 mt-0.5" weight="bold" />
                  <span>Rate changes apply to new entries you log on or after the effective date. They don't retroactively recompute shifts already scheduled on the calendar.</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Effective date</Label>
                  <div className="mt-1.5"><DatePicker testId="pay-change-effective-date" value={form.effective_date} onChange={(d) => setForm({ ...form, effective_date: d })} /></div>
                </div>
                <div>
                  <Label>End date (optional)</Label>
                  <div className="mt-1.5"><DatePicker testId="pay-change-end-date" value={form.end_date} onChange={(d) => setForm({ ...form, end_date: d })} /></div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>New value {form.change_type === "regular_scheduled_hours" ? "(hours)" : ""}</Label>
                  <Input type="number" step="0.01" min="0" data-testid="pay-change-new-value" className="mt-1.5 bg-secondary/50" required
                    value={form.new_value} onChange={(e) => setForm({ ...form, new_value: e.target.value })}
                    placeholder={form.change_type === "annual_salary" ? "130000" : "0"} />
                </div>
                <div>
                  <Label>Previous value (optional)</Label>
                  <Input type="number" step="0.01" min="0" data-testid="pay-change-previous-value" className="mt-1.5 bg-secondary/50"
                    value={form.previous_value} onChange={(e) => setForm({ ...form, previous_value: e.target.value })}
                    placeholder="for reference" />
                </div>
              </div>

              <div>
                <Label>Notes (optional)</Label>
                <Textarea data-testid="pay-change-notes" className="mt-1.5 bg-secondary/50" rows={2}
                  value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="e.g. Acting Sergeant assignment" />
              </div>

              <p className="text-xs text-muted-foreground">Leave the end date blank to make the change permanent until another change replaces it.</p>

              <DialogFooter>
                <Button type="submit" disabled={saveMut.isPending} data-testid="pay-change-submit" className="rounded-md font-semibold">
                  {saveMut.isPending ? "Saving…" : editing ? "Update" : "Schedule"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-card border-border overflow-hidden">
        {changes.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground" data-testid="pay-changes-empty">
            No scheduled changes yet. Add a raise or deduction change and it'll flow into your projections.
          </div>
        ) : changes.map((c) => (
          <div key={c.id} data-testid={`pay-change-row-${c.id}`}
            className="flex items-center gap-3 px-5 py-4 border-b border-border last:border-0 hover:bg-secondary/30 transition-colors duration-200">
            <div className="min-w-0 flex-1">
              <p className="font-semibold truncate">{changeTitle(c)}</p>
              <p className="text-xs text-muted-foreground">
                From {fmtDate(c.effective_date)}{c.end_date ? ` to ${fmtDate(c.end_date)}` : " · permanent"}
                {c.notes ? ` · ${c.notes}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm shrink-0">
              {c.previous_value != null && (
                <span className="text-muted-foreground line-through">
                  {c.change_type === "regular_scheduled_hours" ? `${c.previous_value} h` : money(c.previous_value, currency)}
                </span>
              )}
              {c.previous_value != null && <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" weight="bold" />}
              <span className="font-bold text-sky-400" data-testid={`pay-change-value-${c.id}`}>{valueLabel(c)}</span>
            </div>
            <button onClick={() => openEdit(c)} data-testid={`edit-pay-change-${c.id}`}
              className="text-muted-foreground hover:text-primary transition-colors duration-200"><PencilSimple className="h-4 w-4" weight="bold" /></button>
            <button onClick={() => deleteMut.mutate(c.id)} data-testid={`delete-pay-change-${c.id}`}
              className="text-muted-foreground hover:text-destructive transition-colors duration-200"><Trash className="h-4 w-4" weight="bold" /></button>
          </div>
        ))}
      </Card>
    </div>
  );
}
